import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MaskedLines, FadeIn } from "../../lib/reveal";
import { IMAGES } from "../../data/content";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative flex min-h-[100svh] items-end overflow-hidden bg-bati-ink">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Maison rénovée par BÂTI 360 — toiture zinc, métallerie et aménagement extérieur"
          className="h-full w-full object-cover object-[center_35%]"
          fetchpriority="high"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/25" />

      <div className="relative mx-auto w-full max-w-7xl px-5 pb-24 pt-40 md:pb-32 lg:px-8">
        <FadeIn delay={0.1}>
          <p className="mb-6 flex items-center gap-3 text-[12px] font-bold uppercase tracking-[0.22em] text-white/80">
            <span className="h-px w-10 bg-bati-sky" />
            Entreprise tous corps d'état — Paris & Île-de-France
          </p>
        </FadeIn>
        <h1 data-testid="hero-title" className="font-display text-[13vw] font-extrabold leading-[0.95] tracking-tight text-white sm:text-7xl lg:text-8xl">
          <MaskedLines lines={["Rénover.", "Transformer.", "Construire."]} delay={0.25} />
        </h1>
        <FadeIn delay={0.85} className="mt-12 max-w-xl">
          <p data-testid="hero-subtitle" className="text-base leading-relaxed text-white/85 md:text-lg">
            BÂTI 360 accompagne vos projets de rénovation et de transformation à Paris et en Île-de-France, de l'étude jusqu'à la livraison.
          </p>
        </FadeIn>
        <FadeIn delay={1.05} className="mt-10 flex flex-col gap-4 sm:flex-row">
          <Link
            to="/contact"
            data-testid="hero-cta-devis"
            className="bg-bati-blue px-8 py-4 text-center text-[13px] font-bold uppercase tracking-[0.12em] text-white transition-colors duration-300 hover:bg-bati-sky"
          >
            Demander un devis
          </Link>
          <Link
            to="/realisations"
            data-testid="hero-cta-realisations"
            className="border border-white/40 px-8 py-4 text-center text-[13px] font-bold uppercase tracking-[0.12em] text-white transition-colors duration-300 hover:border-white hover:bg-white hover:text-bati-ink"
          >
            Découvrir nos réalisations
          </Link>
        </FadeIn>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="absolute bottom-8 right-8 hidden text-white/60 lg:block"
      >
        <ArrowDown className="h-5 w-5 animate-bounce" />
      </motion.div>
    </section>
  );
};

export default Hero;
